/**
 * React Context-based Theme System for Venn
 */

import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { Schemes, VennColors } from '../../style/color';
import { type BaseColors, transformCustomColorsToVennColors, type CustomColors } from '../../style/customColors';
import type { DynamicThemeType, Images, Typography, Videos } from '../../style/themes';

// Legacy imports for backwards compatibility
import { getImageTheme } from '../src/getImageTheme';
import { getVideoTheme } from '../src/getVideoTheme';
import { getTypographyTheme } from '../src/getTypographyTheme';
import { getCurrentTheme } from '../src/getCurrentTheme';

/**
 * Complete theme object including colors, assets, and customizations.
 * Compatible with legacy styled-components theme structure.
 */
export interface VennTheme {
  Colors: VennColors; // Atomic color values
  Schemes: Schemes;
  Images: Images;
  Videos: Videos;
  Typography: Typography;
  inPrintMode: boolean;
  inPrintModeOrReportLab: boolean;
  customColors?: CustomColors;
  setCustomColors?: React.Dispatch<React.SetStateAction<CustomColors | undefined>>;
  setDebugColors?: React.Dispatch<React.SetStateAction<VennColors | undefined>>;
}

export interface ThemeContextValue {
  /** Current selected theme ('default' or 'green') */
  currentTheme: DynamicThemeType;

  /** Complete theme object with all properties (colors, images, videos, typography) */
  theme: VennTheme;

  /** Color values for current theme (base colors + custom overrides + debug overrides) */
  colors: VennColors;

  /** Custom color overrides for this organization (persisted, e.g., to backend) */
  customColors: CustomColors | undefined;

  /** Update custom colors (e.g., org admin changes theme colors) */
  updateCustomColors: (colors: CustomColors | undefined) => void;

  /** Debug color overrides for testing/development (temporary, not persisted) */
  debugColors: VennColors | undefined;

  /** Set debug colors for testing */
  setDebugColors: (colors: VennColors | undefined) => void;

  /** Switch to a different base theme */
  switchTheme: (themeName: DynamicThemeType) => void;

  /** Get a color value by dot-notation path (e.g., 'Primary.Main', 'HoldingsColor.SectorColor.Cyclical') */
  getColor: (path: string) => string | undefined;
}

const VennThemeContext = createContext<ThemeContextValue | undefined>(undefined);
VennThemeContext.displayName = 'VennThemeContext';
export { VennThemeContext };

export interface VennThemeProviderProps {
  children: ReactNode;
  initialTheme?: DynamicThemeType;
  initialCustomColors?: CustomColors;
  inPrintMode?: boolean;
}

export const VennThemeProvider: React.FC<VennThemeProviderProps> = ({
  children,
  initialTheme,
  initialCustomColors,
  inPrintMode = false,
}) => {
  // Theme state
  // if we cared about live changes we can subscribe or use the useSessionStorage hook from https://usehooks-ts.com/react-hook/use-session-storage
  const [currentTheme, setCurrentTheme] = useState<DynamicThemeType>(initialTheme || getCurrentTheme());
  const [customColors, setCustomColors] = useState<CustomColors | undefined>(initialCustomColors);
  const [debugColors, setDebugColors] = useState<VennColors | undefined>(undefined);
  // eslint-disable-next-line unused-imports/no-unused-vars
  const [inPrintModeState, setInPrintMode] = useState(inPrintMode);

  /* Append theme class to body to trigger Tailwind overrides from globals.css or remove class when default theme is selected */
  useLayoutEffect(() => {
    const themeClasses = Array.from(document.body.classList).filter((c) => c.startsWith('theme-'));
    themeClasses.forEach((c) => document.body.classList.remove(c));
    if (currentTheme !== 'default') {
      document.body.classList.add(`theme-${currentTheme}`);
    }
  }, [currentTheme]);

  /**
   * Get base color scheme for the current theme.
   * Dynamically imports from default or green theme folders.
   */
  const baseColorScheme = useMemo(() => {
    try {
      // Import base colors for current theme
      if (currentTheme === 'green') {
        const greenModule = require('../green/Colors');
        return greenModule.default || greenModule.NewColorScheme;
      }
      const defaultModule = require('../default/Colors');
      return defaultModule.NewColorScheme || defaultModule.default;
    } catch (error) {
      // Fallback to default
      try {
        const defaultModule = require('../default/Colors');
        return defaultModule.NewColorScheme || defaultModule.default;
      } catch (fallbackError) {
        throw new Error('Unable to load any color scheme');
      }
    }
  }, [currentTheme]);

  /**
   * Calculate final colors: base + custom overrides + debug overrides.
   * Uses the same transformCustomColorsToVennColors logic as useColorTheme.
   */
  const colors = useMemo(() => {
    if (customColors) {
      return transformCustomColorsToVennColors(baseColorScheme as BaseColors, customColors);
    }
    return baseColorScheme as VennColors;
  }, [baseColorScheme, customColors]);

  /**
   * Load theme-specific images, videos, and typography.
   */
  const images = useMemo(() => getImageTheme(currentTheme), [currentTheme]);
  const videos = useMemo(() => getVideoTheme(currentTheme), [currentTheme]);
  const typography = useMemo(() => getTypographyTheme(currentTheme), [currentTheme]);
  const schemes = useMemo(
    (): Schemes => ({
      FactorColors: {
        mainColor: colors.DivergingColor.B3,
        comparisonColor: colors.DivergingColor.A3,
        labelColor: colors.GreyScale.Grey90,
      },
      BarChartColors: {
        mainColor: colors.StudioSubjectColor.A1,
        secondaryColor: colors.StudioSubjectColor.A2,
        comparisonColor: colors.StudioSubjectColor.A3,
        lineColor: colors.GreyScale.Grey20,
      },
      LineChartColors: {
        mainColor: colors.StudioSubjectColor.A1,
        secondaryColor: colors.StudioSubjectColor.A2,
        comparisonColor: colors.StudioSubjectColor.A3,
        lineColor: colors.GreyScale.Grey20,
      },
      Proxy: {
        proxyLine: colors.DEPRECATED_DataLineColor.PaleGold,
        darkProxyLine: colors.DEPRECATED_DataLineColor.Gold,
        extrapolation: colors.DEPRECATED_DataLineColor.PaleBlue,
        subjectLine: colors.Black,
        invertedSubjectLine: colors.White,
        badgeText: colors.White,
        badgeBg: colors.PaleGold,
      },
    }),
    [colors],
  );

  /**
   * Build complete theme object.
   * This is compatible with styled-components' theme structure.
   */
  const theme = useMemo<VennTheme>(
    () => ({
      Colors: colors,
      Images: images,
      Videos: videos,
      Schemes: schemes,
      Typography: typography,
      inPrintMode: inPrintModeState,
      inPrintModeOrReportLab: inPrintModeState,
      customColors,
      setCustomColors,
      setDebugColors,
    }),
    [colors, images, videos, schemes, typography, inPrintModeState, customColors],
  );

  /**
   * Helper function to get nested color values by dot-notation path.
   * Example: 'Primary.Main', 'HoldingsColor.SectorColor.Cyclical'
   */
  const getColor = useCallback(
    (path: string): string | undefined => {
      if (!path) return undefined;

      const parts = path.split('.');
      // Use debug colors if set, otherwise use computed colors
      let current: unknown = debugColors ?? colors;

      for (const part of parts) {
        if (current && typeof current === 'object' && part in (current as Record<string, unknown>)) {
          current = (current as Record<string, unknown>)[part];
        } else {
          return undefined;
        }
      }

      return typeof current === 'string' ? current : undefined;
    },
    [colors, debugColors],
  );

  /**
   * Build context value object.
   */
  const value: ThemeContextValue = useMemo(
    () => ({
      currentTheme,
      theme,
      colors: debugColors ?? colors,
      customColors,
      updateCustomColors: setCustomColors,
      debugColors,
      setDebugColors,
      switchTheme: setCurrentTheme,
      getColor,
    }),
    [colors, currentTheme, customColors, debugColors, getColor, theme],
  );

  return <VennThemeContext.Provider value={value}>{children}</VennThemeContext.Provider>;
};

/**
 * Hook to access the complete theme context.
 * Provides colors, theme name, custom colors, debug colors, and utilities.
 *
 * Must be used within a `<VennThemeProvider>` component.
 *
 * @returns ThemeContextValue containing all theme data and methods
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const {
 *     colors,
 *     currentTheme,
 *     switchTheme,
 *     updateCustomColors,
 *     getColor,
 *   } = useTheme();
 *
 *   return (
 *     <div style={{ color: colors.Primary.Main }}>
 *       Current theme: {currentTheme}
 *       <button onClick={() => switchTheme('green')}>Switch</button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @throws {Error} If used outside of VennThemeProvider
 */
export const useVennTheme = (): ThemeContextValue => {
  const context = useContext(VennThemeContext);
  if (context === undefined) {
    throw new Error(
      'useVennTheme must be used within a <VennThemeProvider>. Make sure your component is wrapped by VennThemeProvider at the app root.',
    );
  }
  return context;
};

/**
 * Hook to access only the colors object.
 * Useful when components only need color values, not other theme properties.
 * Returns the final colors (base + custom overrides + debug overrides).
 *
 * @returns VennColors object with all color definitions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const colors = useColors();
 *   return <div style={{ color: colors.Primary.Main }}>Styled</div>;
 * }
 * ```
 */
export const useColors = (): VennColors => {
  const { colors } = useVennTheme();
  return colors;
};

/**
 * Hook to access only the current theme name.
 * Useful for conditional rendering based on theme selection.
 *
 * @returns Current theme name ('default' | 'green')
 *
 * @example
 * ```tsx
 * function MyComponent() {\n *   const theme = useCurrentTheme();
 *   return <div>{theme === 'green' ? 'Green' : 'Default'}</div>;
 * }
 * ```
 */
export const useCurrentTheme = (): DynamicThemeType => {
  const { currentTheme } = useVennTheme();
  return currentTheme;
};

/**
 * Hook to manage custom color overrides.
 * Useful for organization-specific theme customization features.
 *
 * @returns Object with customColors state and updateCustomColors function
 *
 * @example
 * ```tsx
 * function ThemeSettings() {
 *   const { customColors, updateCustomColors } = useCustomColors();
 *
 *   const handleSave = () => {
 *     updateCustomColors(newColors);
 *     // optionally persist to backend
 *   };
 *
 *   return <button onClick={handleSave}>Save Theme</button>;
 * }
 * ```
 */
export const useCustomColors = () => {
  const { customColors, updateCustomColors } = useVennTheme();
  return { customColors, updateCustomColors };
};

/**
 * Hook to get a single color value by dot-notation path.
 * Useful for getting specific colors without accessing the entire colors object.
 *
 * @param path Dot-notation path to the color (e.g., 'Primary.Main', 'DarkBlue')
 * @returns Hex color string, or undefined if path not found
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const primaryColor = useColorValue('Primary.Main');
 *   const darkBlue = useColorValue('DarkBlue');
 *
 *   return <div style={{ color: darkBlue }}>Text</div>;
 * }
 * ```
 */
export const useColorValue = (path: string): string | undefined => {
  const { getColor } = useVennTheme();
  return getColor(path);
};

/**
 * Hook to manage debug color overrides.
 * Useful for testing/QA to temporarily override colors without affecting custom colors.
 *
 * @returns Object with debugColors state and setDebugColors function
 *
 * @example
 * ```tsx
 * function DebugPanel() {
 *   const { colors, debugColors, setDebugColors } = useDebugColors();
 *
 *   return (
 *     <button
 *       onClick={() =>
 *         setDebugColors({ ...colors, Primary: { ...colors.Primary, Main: '#FF0000' } })
 *       }
 *     >
 *       Test Red Theme
 *     </button>
 *   );
 * }
 * ```
 */
export const useDebugColors = () => {
  const { debugColors, setDebugColors } = useVennTheme();
  return { debugColors, setDebugColors };
};
